import React, { useState, useEffect, useRef } from 'react';
import { api } from '../services/api';

/**
 * Course name input with autocomplete dropdown.
 * Suggestions come from past tee_times (DB) and a single web-search result (Claude).
 *
 * Props:
 *   value      — current courseName string
 *   onChange   — (courseName) called as the user types (parent updates courseName only)
 *   onSelect   — ({ courseName, courseUrl, coursePhone }) called when user picks a suggestion
 *   placeholder
 *   required   — bool
 */
export default function CourseAutocomplete({ value, onChange, onSelect, placeholder, required }) {
  const [suggestions, setSuggestions] = useState({ existing: [], web: null });
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(-1);
  const [lastQuery, setLastQuery] = useState('');
  const wrapRef = useRef(null);
  const interactedRef = useRef(false);

  // Debounced fetch — only after first user interaction
  useEffect(() => {
    if (!interactedRef.current) return;
    const q = (value || '').trim();
    if (q.length < 2) { setSuggestions({ existing: [], web: null }); return; }
    if (q === lastQuery) return;

    const t = setTimeout(async () => {
      setLoading(true);
      try {
        const data = await api.suggestCourses(q);
        setSuggestions(data || { existing: [], web: null });
        setLastQuery(q);
      } catch { /* ignore */ }
      finally { setLoading(false); }
    }, 350);
    return () => clearTimeout(t);
  }, [value, lastQuery]);

  // Close on outside click
  useEffect(() => {
    const onDoc = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  // Build a flat list of selectable items in display order
  const items = [];
  for (const e of (suggestions.existing || [])) {
    items.push({
      kind: 'existing',
      label: e.course_name,
      hint: `used ${e.use_count} time${e.use_count === 1 ? '' : 's'}`,
      data: { courseName: e.course_name, courseUrl: e.course_url || '', coursePhone: e.course_phone || '' },
    });
  }
  if (suggestions.web) {
    items.push({
      kind: 'web',
      label: suggestions.web.courseName,
      hint: suggestions.web.location ? `web • ${suggestions.web.location}` : 'from web',
      data: {
        courseName: suggestions.web.courseName,
        courseUrl: suggestions.web.courseUrl || '',
        coursePhone: suggestions.web.coursePhone || '',
      },
    });
  }
  // "Use as typed" fallback only when current value isn't already an exact match
  const typed = (value || '').trim();
  const exactMatch = items.some(i => i.label.toLowerCase() === typed.toLowerCase());
  if (typed.length >= 2 && !exactMatch) {
    items.push({
      kind: 'typed',
      label: `Use "${typed}" exactly as typed`,
      hint: '',
      data: { courseName: typed, courseUrl: '', coursePhone: '' },
    });
  }

  const pick = (item) => {
    onSelect && onSelect(item.data);
    setOpen(false);
    setHighlight(-1);
  };

  const onKeyDown = (e) => {
    if (!open || items.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlight(h => Math.min(h + 1, items.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlight(h => Math.max(h - 1, 0));
    } else if (e.key === 'Enter' && highlight >= 0) {
      e.preventDefault();
      pick(items[highlight]);
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  };

  return (
    <div ref={wrapRef} style={{ position: 'relative' }}>
      <input
        className="form-input"
        value={value}
        onChange={(e) => { interactedRef.current = true; onChange(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        onKeyDown={onKeyDown}
        placeholder={placeholder || 'e.g. Indian Canyon GC'}
        required={required}
        autoComplete="off"
      />
      {loading && (
        <span style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', fontSize: 11, color: '#9a9a8a', pointerEvents: 'none' }}>
          searching…
        </span>
      )}
      {open && items.length > 0 && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 50,
          marginTop: 2, background: '#fff', border: '1px solid #d8d4c8', borderRadius: 8,
          boxShadow: '0 4px 12px rgba(0,0,0,0.08)', overflow: 'hidden',
        }}>
          {items.map((item, idx) => {
            const isHighlight = idx === highlight;
            const icon = item.kind === 'existing' ? '🟢' : item.kind === 'web' ? '🌐' : '✏️';
            return (
              <div
                key={`${item.kind}-${idx}-${item.label}`}
                onMouseDown={(e) => { e.preventDefault(); pick(item); }}
                onMouseEnter={() => setHighlight(idx)}
                style={{
                  padding: '7px 10px', cursor: 'pointer',
                  background: isHighlight ? '#f0faf4' : '#fff',
                  borderBottom: idx < items.length - 1 ? '0.5px solid #f0ede4' : 'none',
                  display: 'flex', alignItems: 'center', gap: 7,
                }}
              >
                <span style={{ fontSize: 14 }}>{icon}</span>
                <span style={{ fontSize: 13, color: '#0d2b1a', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {item.label}
                </span>
                {item.hint && (
                  <span style={{ fontSize: 10, color: '#9a9a8a', whiteSpace: 'nowrap' }}>{item.hint}</span>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
